import { Package, Session } from "@/api/dtos";
import { Box, ButtonBase, Paper, Radio, alpha, styled, useMediaQuery, useTheme } from "@mui/material";
import { FC } from "react";
import Sessions from "./Sessions";

interface IPackageProps {
    packageInformation: Package;
    index: number;
    total: number;
    selectedPackageId?: number;
    allSessions: Session[];
    onPackageChange: (packageInformation: Package) => void;
}

const PackagePaper = styled(Paper)(({theme}) => ({
    marginBottom: theme.spacing(2)
}))

const PackageFlexBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'selected' && prop !== 'fullWidth'
})<{
    selected?: boolean;
    fullWidth?: boolean;
}>(({ theme, selected, fullWidth }) => ({
    padding: theme.spacing(2),
    width: fullWidth ? '100%' : undefined,
    textAlign: 'left',
    display: 'block',

    [theme.breakpoints.up('md')]: {
        display: 'flex',
        gap: theme.spacing(3),
    },

    [theme.breakpoints.down('md')]: {
        //backgroundColor: selected ? theme.palette.grey[100] : undefined //Mobile selection color
        backgroundColor: selected ? alpha(theme.palette.primary.main, 0.15) : undefined //Mobile selection color
    }
}))

const PackageSelection = styled(Box)(({theme}) => ({
    flex: '1 1 10%',
    minWidth: 24,
    maxWidth: 24,

    [theme.breakpoints.down('md')]: {
        display: 'none'
    }
}))

const PackageRadio = styled(Radio)(({theme}) => ({
    padding: 0
}))

const PackageName = styled(Box)(({theme}) => ({
    ...theme.typography.subtitle1,
    flex: '1 1 40%'
}))

const PackageDescription = styled(Box)(({theme}) => ({
    flex: '1 1 40%',

    [theme.breakpoints.down('md')]: {
        ...theme.typography.caption
    }
}))

const PackagePrice = styled(Box)(({theme}) => ({

    flex: '1 1 10%',
    minWidth: 60,
    maxWidth: 60,
    //textAlign: 'right',

    [theme.breakpoints.down('md')]: {
        minWidth: 'unset',
        maxWidth: 'unset',
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
        textAlign: 'right',
    }
}))

const PackagePriceLineThrough = styled(Box)(({theme}) => ({
    textDecoration: 'line-through',
    textDecorationColor: theme.palette.error.main
}))

const PackageView: FC<IPackageProps> = ({
    packageInformation,
    selectedPackageId,
    allSessions,
    onPackageChange
}) => {
    const theme = useTheme(); 

    const selected = selectedPackageId === packageInformation.id;
    
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

    const onPackageSelected = () => {
        onPackageChange(packageInformation);
    };

    return (
        <PackagePaper>
            <PackageFlexBox 
                selected={selected}
                onClick={
                    isMdDown ?
                        onPackageSelected :
                        undefined
                }
                component={
                    isMdDown ?
                        ButtonBase :
                        undefined
                }
                fullWidth
            >
                <PackageSelection>
                    <PackageRadio 
                        color='primary' 
                        checked={selected}
                        onChange={onPackageSelected}
                    />
                </PackageSelection>
                <PackageName>{packageInformation.name}</PackageName>
                <PackageDescription>{packageInformation.description}</PackageDescription>
                <PackagePrice>
                    { !packageInformation.discountPrice && (
                        <>
                            ${packageInformation.price ?? 0}
                        </>
                    )}
                    { !!packageInformation.discountPrice && (
                        <>
                            <PackagePriceLineThrough>${packageInformation.price}</PackagePriceLineThrough>
                            ${packageInformation.discountPrice}
                        </>
                    )}
                </PackagePrice>
            </PackageFlexBox>
            {
                selected && (
                    <Sessions 
                        packageInformation={packageInformation} 
                        allSessions={allSessions}
                    />
                )
            }
        </PackagePaper>
    )
}

export default PackageView;